/**
 * HolidayImportDialog Component
 * Dialog for importing national holidays
 *
 * @version 2025.1.0
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import { Download } from '@mui/icons-material';
import { HolidayImportResult, getAvailableYears } from '../../../types/holiday';

interface HolidayImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (year: number) => Promise<HolidayImportResult>;
}

const HolidayImportDialog: React.FC<HolidayImportDialogProps> = ({
  open,
  onClose,
  onImport,
}) => {
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<HolidayImportResult | null>(null);

  const years = getAvailableYears();

  const handleImport = async () => {
    setError(null);
    setResult(null);
    setLoading(true);

    try {
      const importResult = await onImport(year);
      setResult(importResult);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao importar feriados';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setResult(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Download />
          <Typography variant="h6">Importar Feriados Nacionais</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Selecione o ano para importar os feriados nacionais brasileiros.
          Feriados já existentes serão ignorados.
        </Typography>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Ano</InputLabel>
          <Select
            value={year}
            onChange={(e) => setYear(e.target.value as number)}
            label="Ano"
          >
            {years.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {result && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{result.imported}</strong> feriado(s) importado(s)
            </Typography>
            {result.skipped > 0 && (
              <Typography variant="body2" color="text.secondary">
                {result.skipped} feriado(s) já existente(s) foram ignorados.
              </Typography>
            )}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {result ? 'Fechar' : 'Cancelar'}
        </Button>
        {!result && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <Download />}
          >
            {loading ? 'Importando...' : 'Importar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default HolidayImportDialog;
